/* ReadingRoom — a fullscreen, spotlit space for consulting a lyric booklet.
 *
 * UX intent (from user brief):
 *   Screen fades. Reading Room: Dark. Single spotlight. Booklet. Nothing else.
 *   User flips pages. When finished: Return Artifact.
 *
 * Anti-copy protections applied:
 *   • Right-click / context menu blocked inside the modal
 *   • Image drag blocked
 *   • Text selection disabled
 *   • Print-media CSS hides the booklet (see ReadingRoom.css)
 *   • Diagonal watermark overlaid on every page
 *   • Keyboard shortcut hint: ArrowLeft / ArrowRight / Escape
 *
 * These stop casual copying. NO client-side technique can prevent a
 * determined attacker from screenshotting or screen-recording — this is
 * a fundamental property of the browser and applies to every website
 * on the internet.
 */
import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import "./ReadingRoom.css";

const WATERMARK_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="520" height="260" viewBox="0 0 520 260">
     <text x="0" y="150"
           font-family="Georgia, serif"
           font-size="18"
           fill="rgba(199,168,106,0.18)"
           letter-spacing="6"
           transform="rotate(-24 260 130)">
       © NEO BRIGHTWELL · NEOVERSE ARCHIVE
     </text>
   </svg>`
);

export function ReadingRoom({ open, onClose, pages, albumTitle }) {
  const [index, setIndex] = useState(0);

  // Reset to first page whenever the room is (re)opened.
  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  const total = pages?.length || 0;

  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setIndex((i) => Math.min(total - 1, i + 1)),
    [total]
  );

  // Keyboard navigation while the room is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
      else if (e.key === "Home") { e.preventDefault(); setIndex(0); }
      else if (e.key === "End") { e.preventDefault(); setIndex(Math.max(0, total - 1)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, goPrev, goNext, total]);

  // Lock body scroll while the room is open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (typeof document === "undefined") return null;

  const currentPage = pages?.[index];

  return ReactDOM.createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="reading-room"
          role="dialog"
          aria-modal="true"
          aria-label={`Lyric booklet reading room${albumTitle ? " — " + albumTitle : ""}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          onContextMenu={(e) => e.preventDefault()}
        >
          {/* Spotlight vignette */}
          <div className="reading-room__spotlight" aria-hidden="true" />

          {/* Booklet page — fades in slightly delayed for stagey feel */}
          <motion.div
            key={index}
            className="reading-room__stage"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            {currentPage && (
              <img
                src={currentPage}
                alt={`Booklet page ${index + 1} of ${total}${albumTitle ? " — " + albumTitle : ""}`}
                className="reading-room__page"
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
              />
            )}
            {/* Watermark overlay */}
            <div
              aria-hidden="true"
              className="reading-room__watermark"
              style={{
                backgroundImage: `url("data:image/svg+xml;utf8,${WATERMARK_SVG}")`,
              }}
            />
          </motion.div>

          {/* Left / right invisible tap zones (mobile-friendly) */}
          {index > 0 && (
            <button
              type="button"
              className="reading-room__nav reading-room__nav--left"
              onClick={goPrev}
              aria-label="Previous page"
            >
              <ChevronLeft size={22} />
            </button>
          )}
          {index < total - 1 && (
            <button
              type="button"
              className="reading-room__nav reading-room__nav--right"
              onClick={goNext}
              aria-label="Next page"
            >
              <ChevronRight size={22} />
            </button>
          )}

          {/* Page counter */}
          <div className="reading-room__counter" aria-live="polite">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>

          {/* Return artifact */}
          <button
            type="button"
            onClick={onClose}
            data-testid="return-artifact-btn"
            className="reading-room__return"
            aria-label="Return artifact and close the reading room"
          >
            <X size={13} />
            <span>Return Artifact</span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
